import { z } from "zod";
import { postDocSchema } from "./post";
import {
  DATE_PATTERN,
  HEX_COLOR_PATTERN,
  TIME_PATTERN,
  cta,
  href,
  id,
  image,
  price,
  requiredText,
  text,
  youtubeUrl,
} from "./primitives";

/**
 * Section catalogue: every section type with its allowed variants and a strict content schema.
 * Adding a section type = add an entry here + a component in @baze/engine + defaults in templates.
 */

const eyebrow = text(60).optional();
const dataSource = z.strictObject({ type: z.literal("static") }).optional();

/** What a store product is; drives its icon, copy and detail fields. */
export const PRODUCT_KINDS = ["ebook", "course", "asset", "audio", "other"] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];
/** Store product slug (its page is /produk/<slug>). */
export const PRODUCT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,79}$/;
const productSlug = () => z.string().regex(PRODUCT_SLUG_PATTERN);

/**
 * Where a products section gets its items: the owner's own list (`static`, the default) or the site's
 * Toko (`store`): active products, optionally only some kinds or a hand-picked, ordered list. With
 * `store`, `items` stays in the definition untouched, so switching back restores them.
 */
const productsSource = z
  .discriminatedUnion("type", [
    z.strictObject({ type: z.literal("static") }),
    z.strictObject({
      type: z.literal("store"),
      kinds: z.array(z.enum(PRODUCT_KINDS)).max(PRODUCT_KINDS.length).optional(),
      slugs: z.array(productSlug()).max(12).optional(),
      limit: z.number().int().min(1).max(24).optional(),
    }),
  ])
  .optional();

const items = <T extends z.ZodType>(item: T, max: number) => z.array(item).max(max);

export const DONATION_FIELD_TYPES = ["text", "phone", "textarea", "number", "select"] as const;

/** One owner-defined question on the donation form; its answer is sent as "label: value". */
const donationField = z.strictObject({
  label: requiredText(40),
  type: z.enum(DONATION_FIELD_TYPES),
  required: z.boolean().optional(),
  placeholder: text(80).optional(),
  /** Choices for "select". */
  options: items(requiredText(60), 12).optional(),
});

export const sectionContent = {
  navbar: z.strictObject({
    ctaLabel: text(40).optional(),
    showCta: z.boolean().optional(),
    /** Where the button goes. Absent = automatic (site WhatsApp, else #kontak, else phone). */
    ctaKind: z.enum(["whatsapp", "link"]).optional(),
    /** Page (/slug), section (#anchor) or any safe URL; used when ctaKind is "link". */
    ctaHref: href().optional(),
    /** WhatsApp message template; used when ctaKind is "whatsapp". */
    ctaMessage: text(300).optional(),
    /** Fixed bottom bar with the nav links on phones (app-style). */
    mobileBar: z.boolean().optional(),
    /** Site name beside the logo. Omitted means shown. With no logo, the name stays so the bar is not empty. */
    showTitle: z.boolean().optional(),
  }),
  hero: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    image: image().optional(),
    /** Extra slides after `image`. Only the slideshow variant shows them. */
    images: items(image(), 8).optional(),
    primaryCta: cta().optional(),
    secondaryCta: cta().optional(),
  }),
  about: z.strictObject({
    eyebrow,
    title: requiredText(120),
    body: text(1500),
    image: image().optional(),
    highlights: items(requiredText(80), 6).optional(),
  }),
  services: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        title: requiredText(80),
        description: text(320).optional(),
        price: price().optional(),
        priceNote: text(40).optional(),
        image: image().optional(),
        /** Card button; unset = the WhatsApp "Tanya" link. A "link" can open another page (e.g. /donasi). */
        cta: cta().optional(),
      }),
      24,
    ),
  }),
  menu: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    categories: items(
      z.strictObject({
        name: requiredText(60),
        items: items(
          z.strictObject({
            name: requiredText(80),
            description: text(240).optional(),
            price: price().optional(),
            image: image().optional(),
            tags: items(requiredText(20), 4).optional(),
            /** Card button; unset = the WhatsApp "Pesan" link. */
            cta: cta().optional(),
          }),
          40,
        ),
      }),
      12,
    ),
    orderMessage: text(300).optional(),
    dataSource,
  }),
  products: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        name: requiredText(80),
        description: text(320).optional(),
        price: price().optional(),
        priceNote: text(40).optional(),
        image: image().optional(),
        badge: text(20).optional(),
        /** Artwork / handmade details (all optional): shown as a spec list on the card. */
        artist: text(80).optional(),
        medium: text(80).optional(),
        size: text(40).optional(),
        year: text(10).optional(),
        /** Availability. "sold" shows a chip and hides the order button. */
        status: z.enum(["available", "reserved", "sold"]).optional(),
        /** Card button; unset = the WhatsApp "Pesan" link. */
        cta: cta().optional(),
      }),
      48,
    ),
    orderMessage: text(300).optional(),
    dataSource: productsSource,
  }),
  gallery: z.strictObject({
    eyebrow,
    title: text(120).optional(),
    images: items(image(), 24),
  }),
  testimonials: z.strictObject({
    eyebrow,
    title: requiredText(120),
    items: items(
      z.strictObject({
        quote: requiredText(500),
        name: requiredText(60),
        role: text(60).optional(),
        rating: z.number().int().min(1).max(5).optional(),
        /** True for placeholder content shipped by a template. Publishing warns while any remain. */
        sample: z.boolean().optional(),
      }),
      12,
    ),
  }),
  faq: z.strictObject({
    eyebrow,
    title: requiredText(120),
    items: items(z.strictObject({ question: requiredText(160), answer: requiredText(800) }), 20),
  }),
  stats: z.strictObject({
    eyebrow,
    title: text(120).optional(),
    items: items(z.strictObject({ value: requiredText(20), label: requiredText(60) }), 6),
  }),
  "opening-hours": z.strictObject({
    eyebrow,
    title: requiredText(120),
    note: text(200).optional(),
  }),
  location: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    showMap: z.boolean().optional(),
  }),
  contact: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    message: text(300).optional(),
    showWhatsapp: z.boolean().optional(),
    showPhone: z.boolean().optional(),
    showEmail: z.boolean().optional(),
    showSocial: z.boolean().optional(),
  }),
  pricing: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    plans: items(
      z.strictObject({
        name: requiredText(60),
        price: price().optional(),
        priceNote: text(40).optional(),
        period: text(20).optional(),
        features: items(requiredText(80), 12),
        highlighted: z.boolean().optional(),
        cta: cta().optional(),
      }),
      6,
    ),
  }),
  cta: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    button: cta(),
  }),
  timeline: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        /** Short marker: a step number, a year, or a date ("Langkah 1", "2019", "Juni 2024"). */
        label: text(40).optional(),
        title: requiredText(80),
        description: text(400).optional(),
        image: image().optional(),
      }),
      12,
    ),
  }),
  events: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        name: requiredText(60),
        date: z.string().regex(DATE_PATTERN).optional(),
        startTime: z.string().regex(TIME_PATTERN).optional(),
        endTime: z.string().regex(TIME_PATTERN).optional(),
        /** Free text after the time, e.g. "WIB", "s.d. selesai". */
        timeNote: text(40).optional(),
        venue: text(120).optional(),
        address: text(240).optional(),
        mapUrl: href().optional(),
        description: text(320).optional(),
      }),
      6,
    ),
    /** Show an "add to Google Calendar" link on dated events. */
    showCalendar: z.boolean().optional(),
  }),
  team: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        name: requiredText(80),
        role: text(80).optional(),
        description: text(320).optional(),
        image: image().optional(),
        instagram: z
          .string()
          .regex(/^[A-Za-z0-9._]{1,30}$/)
          .optional(),
      }),
      12,
    ),
  }),
  quote: z.strictObject({
    eyebrow,
    text: requiredText(600),
    cite: text(120).optional(),
  }),
  countdown: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** The moment counted down to: local wall-clock date and time in the zone from `timeNote`. */
    date: z.string().regex(DATE_PATTERN),
    time: z.string().regex(TIME_PATTERN).optional(),
    /** "WIB" / "WITA" / "WIT" picks the zone (WIB by default). */
    timeNote: text(40).optional(),
    /** Shown once the moment has passed. */
    doneText: text(120).optional(),
    showCalendar: z.boolean().optional(),
  }),
  gift: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    accounts: items(
      z.strictObject({
        provider: requiredText(40),
        name: requiredText(60),
        number: z
          .string()
          .min(1)
          .max(40)
          .regex(/^[0-9A-Za-z .+-]+$/),
      }),
      6,
    ),
    /** Address for physical gifts. */
    address: text(300).optional(),
  }),
  /**
   * WhatsApp form (donations, registrations, orders…): the owner's questions, then optionally an amount and a
   * choice of account. Nothing is stored: the live page sends the answers to the site's WhatsApp as a prefilled
   * message. ?untuk= (from a card link) names the program or item.
   */
  donation: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    accounts: items(
      z.strictObject({
        provider: requiredText(40),
        name: requiredText(60),
        number: z
          .string()
          .min(1)
          .max(40)
          .regex(/^[0-9A-Za-z .+-]+$/),
      }),
      6,
    ),
    /** Quick amounts in rupiah, shown as chips above the amount field. */
    presets: items(z.number().int().min(1000).max(1_000_000_000), 6).optional(),
    /** The donor's questions, in order (amount and account are always asked after them). Unset = name, WhatsApp, address. */
    fields: items(donationField, 12).optional(),
    /** Only without `fields`: ask for the donor's address (default true). */
    askAddress: z.boolean().optional(),
    /** Ask for an amount in rupiah (default true). */
    askAmount: z.boolean().optional(),
    /** Let the visitor pick one of `accounts` (default true; nothing shows without accounts). */
    askBank: z.boolean().optional(),
    /** Send button text (default "Kirim via WhatsApp"). */
    submitLabel: text(40).optional(),
    /** Label of the read-only field filled from ?untuk= (default "Program"), e.g. "Produk" or "Kelas". */
    programLabel: text(30).optional(),
    /** Opening line of the WhatsApp message; the answers follow it. {{nama_usaha}} is allowed. */
    message: text(300).optional(),
  }),
  /** Guestbook: visitors leave a name and a message; the live page loads and posts them at runtime. */
  wishes: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Also ask "Hadir / Tidak hadir" next to the message (for invitations without an RSVP section). */
    askAttendance: z.boolean().optional(),
  }),
  /** Attendance confirmation: name, attending, number of people. One answer per name, editable by the guest. */
  rsvp: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Last day to answer (local date); the form closes after it. */
    deadline: z.string().regex(DATE_PATTERN).optional(),
    /** Most people one answer may bring, including the guest (default 2). */
    maxPax: z.number().int().min(1).max(10).optional(),
    /** Show a free-text note field (dietary needs, a message). */
    askNote: z.boolean().optional(),
  }),
  /** Newest blog posts (the posts live outside the definition; the runtime passes them in). */
  "blog-latest": z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** How many posts (default 3). */
    count: z.number().int().min(1).max(6).optional(),
    /** Text of the link to /blog, e.g. "Semua artikel". */
    linkLabel: text(40).optional(),
  }),
  links: z.strictObject({
    eyebrow,
    title: text(120).optional(),
    items: items(
      z.strictObject({
        label: requiredText(60),
        href: href(),
        description: text(120).optional(),
        /** Shown in the primary colour, e.g. the newest video or main shop. */
        highlight: z.boolean().optional(),
        /** Affiliate / paid link: rel="sponsored" and an "Afiliasi" badge. */
        sponsored: z.boolean().optional(),
      }),
      20,
    ),
  }),
  /** "Dipercaya oleh": partner and client logos (a name alone renders as a wordmark until a logo is added). */
  logos: z.strictObject({
    eyebrow,
    title: text(120).optional(),
    items: items(
      z.strictObject({
        name: requiredText(60),
        logo: image().optional(),
        url: href().optional(),
      }),
      30,
    ),
    /** Logos in grey, back to colour on hover: a calmer strip when brand colours clash. */
    grayscale: z.boolean().optional(),
  }),
  picks: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Affiliate links (default true): rel="sponsored" and a visible disclosure line. */
    affiliate: z.boolean().optional(),
    disclosure: text(240).optional(),
    /** Button text, e.g. "Lihat proyek". Defaults to "Lihat di <toko>". */
    linkLabel: text(30).optional(),
    items: items(
      z.strictObject({
        name: requiredText(80),
        description: text(240).optional(),
        image: image().optional(),
        price: price().optional(),
        priceNote: text(40).optional(),
        href: href(),
        store: text(30).optional(),
        badge: text(20).optional(),
      }),
      24,
    ),
  }),
  /**
   * YouTube videos, embedded by id on youtube-nocookie.com. A video without a link yet is a
   * placeholder in the editor and is left out of the live site.
   */
  videos: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        url: youtubeUrl().optional(),
        title: text(120).optional(),
        description: text(240).optional(),
      }),
      12,
    ),
  }),
  /**
   * Learning material: groups (Pertemuan, Modul, Bab) of topics. Each topic opens in place as a
   * detail view (its own #link) with text, a checklist, a PDF link and a YouTube video.
   */
  lessons: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Search box and group filter chips above the list (both default on). */
    showSearch: z.boolean().optional(),
    showFilter: z.boolean().optional(),
    /** Topics per page in the list (omitted = 8; 0 = one long list). */
    perPage: z.number().int().min(0).max(50).optional(),
    searchPlaceholder: text(60).optional(),
    /** Button texts, e.g. "Buka PDF", "Tonton di YouTube", "Kembali". */
    pdfLabel: text(30).optional(),
    videoLabel: text(30).optional(),
    backLabel: text(30).optional(),
    /** Reminder shown at the end of every topic, e.g. "Ingat, Bunda". */
    note: z.strictObject({ title: text(60).optional(), body: requiredText(400) }).optional(),
    groups: items(
      z.strictObject({
        /** Short marker: "Pertemuan 1", "Modul 2", "Bab 3". */
        label: text(30).optional(),
        title: requiredText(80),
        description: text(240).optional(),
        topics: items(
          z.strictObject({
            title: requiredText(100),
            summary: text(320).optional(),
            image: image().optional(),
            points: items(requiredText(120), 10).optional(),
            body: text(4000).optional(),
            /** Rich content (headings, lists, links, images); shown instead of `body` when set. */
            doc: postDocSchema.optional(),
            /** Link to the PDF (e.g. Google Drive). */
            pdf: href().optional(),
            /** YouTube video, embedded in the topic. */
            video: youtubeUrl().optional(),
          }),
          30,
        ),
      }),
      12,
    ),
  }),
  /**
   * One product, sold from this page (e-book, course): a headline, the product's cover and price from
   * the Toko, its highlights (or `bullets`) and a buy button. `price`/`image` are what shows when the
   * store has no such product (template demos, thumbnails, a store that is off).
   */
  "product-feature": z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(400).optional(),
    productSlug: productSlug().optional(),
    bullets: z.array(requiredText(140)).max(6).optional(),
    guarantee: text(160).optional(),
    ctaLabel: text(30).optional(),
    showTiers: z.boolean().optional(),
    price: price().optional(),
    image: image().optional(),
  }),
  footer: z.strictObject({
    text: text(300).optional(),
    showSocial: z.boolean().optional(),
    showHours: z.boolean().optional(),
    showAddress: z.boolean().optional(),
    /** Page links. Omitted means shown. */
    showNav: z.boolean().optional(),
    /** Phone, email, and WhatsApp. Address stays on `showAddress`. Omitted means shown. */
    showContact: z.boolean().optional(),
    /** Site name under the logo. Omitted means shown. With no logo, the name stays. */
    showTitle: z.boolean().optional(),
    /**
     * How the columns sit. Omitted means spread on the columns variant and centered on the simple one.
     * "spread" on the simple variant stays centered, because that variant is a single stack.
     */
    align: z.enum(["start", "center", "spread"]).optional(),
  }),
} as const;

export type SectionType = keyof typeof sectionContent;
export const SECTION_TYPES = Object.keys(sectionContent) as SectionType[];

export const SECTION_VARIANTS = {
  navbar: ["simple", "centered", "floating"],
  hero: [
    "split",
    "centered",
    "image-background",
    "minimal",
    "arch",
    "editorial",
    "profile",
    "celebration",
    "cover",
    "marquee",
    "collage",
    "stage",
    "margin",
    "envelope",
    "boarding-pass",
    "newspaper",
    "glasshouse",
    "slideshow",
  ],
  about: ["split", "centered", "feature"],
  services: ["cards", "list", "alternating", "index", "chips"],
  menu: ["cards", "list"],
  products: ["grid", "cards", "tracks"],
  gallery: ["grid", "mosaic", "carousel", "masonry", "polaroid", "slideshow"],
  testimonials: ["cards", "single", "carousel", "chat"],
  faq: ["accordion", "two-column"],
  stats: ["row", "cards"],
  "opening-hours": ["table", "card"],
  location: ["card", "map"],
  contact: ["whatsapp-card", "split"],
  pricing: ["cards", "table"],
  cta: ["banner", "split", "spotlight", "celebration", "marquee"],
  timeline: ["vertical", "alternating", "steps"],
  events: ["cards", "stacked", "agenda"],
  team: ["grid", "couple"],
  quote: ["centered", "ornate"],
  countdown: ["boxes", "minimal"],
  gift: ["cards"],
  donation: ["card", "split"],
  wishes: ["cards", "stream"],
  rsvp: ["card", "split"],
  "blog-latest": ["cards", "list"],
  links: ["stack", "grid"],
  picks: ["grid", "list", "carousel", "bento", "showcase", "panorama"],
  logos: ["slider", "marquee", "grid"],
  videos: ["featured", "grid"],
  lessons: ["cards", "list"],
  "product-feature": ["spotlight", "split", "stack"],
  footer: ["simple", "columns"],
} as const satisfies Record<SectionType, readonly [string, ...string[]]>;

export type SectionVariant<T extends SectionType = SectionType> = (typeof SECTION_VARIANTS)[T][number];

export const SECTION_TONES = ["default", "surface", "inverted"] as const;
export type SectionTone = (typeof SECTION_TONES)[number];

const hexColor = () => z.string().regex(HEX_COLOR_PATTERN);

// ── Style vocabulary shared by site-wide overrides, section overrides and text styles ──
export const HEADING_CASES = ["normal", "uppercase"] as const;
export const HEADING_WEIGHTS = [400, 500, 600, 700, 800] as const;
export const HEADING_TRACKINGS = ["tight", "normal", "wide"] as const;
export const RADIUS_PRESETS = ["sharp", "soft", "round", "pill"] as const;
/** Same list as TEXTURES in @baze/catalog (the design token); kept here so overrides validate. */
export const THEME_TEXTURES = ["none", "grain", "grid", "dots", "aurora", "paper", "halftone", "night"] as const;
/** Same list as MOTION_STYLES in @baze/catalog; kept here so the site-wide motion override validates. */
export const THEME_MOTIONS = ["subtle", "cinematic", "playful", "editorial", "dreamy", "bold", "kinetic", "parallax", "paper", "retro", "celestial", "pixel"] as const;

/** Corner presets as the four radius tokens (used by resolveTheme and by section overrides in the engine). */
export const RADIUS_VALUES: Record<(typeof RADIUS_PRESETS)[number], { sm: string; md: string; lg: string; button: string }> = {
  sharp: { sm: "0", md: "0.125rem", lg: "0.25rem", button: "0.125rem" },
  soft: { sm: "0.25rem", md: "0.5rem", lg: "0.875rem", button: "0.5rem" },
  round: { sm: "0.5rem", md: "1rem", lg: "1.75rem", button: "1rem" },
  pill: { sm: "0.75rem", md: "1.25rem", lg: "2rem", button: "999px" },
};
export const TRACKING_VALUES: Record<(typeof HEADING_TRACKINGS)[number], string> = { tight: "-0.035em", normal: "0", wide: "0.06em" };

const headingWeight = () => z.number().refine((n) => (HEADING_WEIGHTS as readonly number[]).includes(n));

/** The theme, overridden for one section (cascades to everything inside it). */
export const sectionThemeSchema = z.strictObject({
  primary: hexColor().optional(),
  accent: hexColor().optional(),
  fontPair: id().optional(),
  headingWeight: headingWeight().optional(),
  headingCase: z.enum(HEADING_CASES).optional(),
  headingTracking: z.enum(HEADING_TRACKINGS).optional(),
  radius: z.enum(RADIUS_PRESETS).optional(),
});
export type SectionTheme = z.infer<typeof sectionThemeSchema>;

export const TEXT_SIZES = ["xs", "sm", "md", "lg", "xl", "2xl"] as const;
export const TEXT_SIZE_SCALE: Record<(typeof TEXT_SIZES)[number], number> = { xs: 0.8, sm: 0.9, md: 1, lg: 1.15, xl: 1.3, "2xl": 1.5 };
export const TEXT_WEIGHTS = [300, 400, 500, 600, 700, 800, 900] as const;
export const TEXT_CASES = ["none", "uppercase", "lowercase", "capitalize"] as const;
export const TEXT_ALIGNS = ["start", "center", "end"] as const;

/** One text's own look (a title, a label, every item name…). Absent keys keep the design's. */
export const textStyleSchema = z.strictObject({
  /** Catalog font id. */
  font: id().optional(),
  size: z.enum(TEXT_SIZES).optional(),
  weight: z.number().refine((n) => (TEXT_WEIGHTS as readonly number[]).includes(n)).optional(),
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
  case: z.enum(TEXT_CASES).optional(),
  tracking: z.enum(HEADING_TRACKINGS).optional(),
  color: hexColor().optional(),
  align: z.enum(TEXT_ALIGNS).optional(),
});
export type TextStyle = z.infer<typeof textStyleSchema>;

/**
 * Which text a style is for: a content path ("content.title", "content.items.2.name"), or the same with
 * `*` for every item of a list ("content.items.*.name"). Exact paths win over `*` ones.
 */
export const TEXT_STYLE_KEY_PATTERN = /^content(?:\.(?:[a-zA-Z][a-zA-Z0-9]{0,39}|\d{1,3}|\*)){1,6}$/;

export const SECTION_BG_KINDS = ["solid", "gradient", "mesh"] as const;
export const GRADIENT_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315] as const;
export const SECTION_PATTERNS = ["none", "dots", "grid", "lines", "noise"] as const;
export const HEADING_SCALES = ["sm", "md", "lg", "xl"] as const;
/** Ways to make a phrase of the section title stand out. */
export const TEXT_EFFECTS = ["highlight", "underline", "wavy", "circle", "gradient", "italic", "outline", "box"] as const;
export type TextEffect = (typeof TEXT_EFFECTS)[number];

/** How a section enters the viewport; absent = the design's motion style. */
export const SECTION_ENTERS = ["none", "fade", "rise", "zoom", "blur", "slide-left", "slide-right", "flip", "curtain", "cascade", "typewriter"] as const;
export type SectionEnter = (typeof SECTION_ENTERS)[number];
/** Interactive effects a section can opt into (see EFFECTS_FOR for where each one applies). */
export const SECTION_EFFECTS = ["tilt", "magnetic", "spotlight", "countup", "parallax", "float", "shine"] as const;
export type SectionEffect = (typeof SECTION_EFFECTS)[number];
export const MOTION_SPEEDS = ["slow", "normal", "fast"] as const;

const CARD_EFFECTS = ["tilt", "spotlight", "shine", "magnetic"] as const;
const CARD_SECTIONS = ["services", "menu", "products", "pricing", "testimonials", "team", "events", "picks", "lessons", "blog-latest", "links", "gift", "product-feature"];
/**
 * The effects that do something on each section type. Others are ignored when rendering (not a
 * validation error), so a definition written by the AI or copied between sections never breaks.
 */
export const EFFECTS_FOR: Record<string, readonly SectionEffect[]> = {
  ...Object.fromEntries(CARD_SECTIONS.map((type) => [type, CARD_EFFECTS])),
  stats: ["countup", "tilt", "spotlight", "shine"],
  countdown: ["float"],
  hero: ["parallax", "magnetic", "float"],
  about: ["parallax", "float", "tilt"],
  gallery: ["parallax", "tilt"],
  cta: ["magnetic", "spotlight", "float"],
  contact: ["magnetic"],
};

/** Owner styling for one section, on top of the theme (all optional; absent = the design's look). */
export const sectionStyleSchema = z.strictObject({
  bg: z
    .discriminatedUnion("kind", [
      z.strictObject({ kind: z.literal("solid"), color: hexColor() }),
      z.strictObject({
        kind: z.literal("gradient"),
        from: hexColor(),
        to: hexColor(),
        angle: z.number().refine((n) => (GRADIENT_ANGLES as readonly number[]).includes(n)),
      }),
      z.strictObject({ kind: z.literal("mesh"), a: hexColor(), b: hexColor(), c: hexColor() }),
    ])
    .optional(),
  pattern: z.enum(SECTION_PATTERNS).optional(),
  /** Text colour; "auto" picks dark or light text for the background. */
  text: z.union([z.literal("auto"), hexColor()]).optional(),
  align: z.enum(["start", "center"]).optional(),
  headingScale: z.enum(HEADING_SCALES).optional(),
  /**
   * A phrase of the title drawn with an effect (marker, underline…). Stored as text, not offsets, so
   * editing the title keeps it where the words still are; `nth` picks a repeated phrase (1 = first).
   */
  /** Colours, fonts, headings and corners for this section only. */
  theme: sectionThemeSchema.optional(),
  /** Per-text styles, keyed by content path (see TEXT_STYLE_KEY_PATTERN). */
  texts: z
    .record(z.string().regex(TEXT_STYLE_KEY_PATTERN), textStyleSchema)
    .refine((r) => Object.keys(r).length <= 60, "Terlalu banyak gaya teks di satu section.")
    .optional(),
  emphasis: z
    .strictObject({
      text: requiredText(120),
      nth: z.number().int().min(1).max(20).optional(),
      effect: z.enum(TEXT_EFFECTS),
      color: hexColor().optional(),
    })
    .optional(),
  /** Entrance animation, interactive effects and speed for this section (live site only). */
  motion: z
    .strictObject({
      enter: z.enum(SECTION_ENTERS).optional(),
      effects: z.array(z.enum(SECTION_EFFECTS)).max(4).optional(),
      speed: z.enum(MOTION_SPEEDS).optional(),
    })
    .optional(),
});
export type SectionStyle = z.infer<typeof sectionStyleSchema>;
export type SectionBackground = NonNullable<SectionStyle["bg"]>;

const sectionFor = <T extends SectionType>(type: T) =>
  z.strictObject({
    id: id(),
    type: z.literal(type),
    variant: z.enum(SECTION_VARIANTS[type] as unknown as [string, ...string[]]),
    /** URL fragment for in-page navigation. Unique per page. */
    anchor: id(),
    navLabel: text(30).optional(),
    showInNav: z.boolean().optional(),
    hidden: z.boolean().optional(),
    tone: z.enum(SECTION_TONES).optional(),
    style: sectionStyleSchema.optional(),
    content: sectionContent[type],
  });

export const sectionSchema = z.discriminatedUnion("type", [
  sectionFor("navbar"),
  sectionFor("hero"),
  sectionFor("about"),
  sectionFor("services"),
  sectionFor("menu"),
  sectionFor("products"),
  sectionFor("gallery"),
  sectionFor("testimonials"),
  sectionFor("faq"),
  sectionFor("stats"),
  sectionFor("opening-hours"),
  sectionFor("location"),
  sectionFor("contact"),
  sectionFor("pricing"),
  sectionFor("cta"),
  sectionFor("timeline"),
  sectionFor("events"),
  sectionFor("team"),
  sectionFor("quote"),
  sectionFor("countdown"),
  sectionFor("gift"),
  sectionFor("donation"),
  sectionFor("wishes"),
  sectionFor("rsvp"),
  sectionFor("blog-latest"),
  sectionFor("links"),
  sectionFor("picks"),
  sectionFor("logos"),
  sectionFor("videos"),
  sectionFor("lessons"),
  sectionFor("product-feature"),
  sectionFor("footer"),
]);

export type SectionNode = z.infer<typeof sectionSchema>;
export type SectionOf<T extends SectionType> = Extract<SectionNode, { type: T }>;
export type SectionContent<T extends SectionType> = z.infer<(typeof sectionContent)[T]>;

export const hrefSchema = href();

export type DonationField = z.infer<typeof donationField>;

/** The questions a donation form asks: its own list, or the defaults (name, WhatsApp, and address unless turned off). */
export function donationFields(content: { fields?: DonationField[]; askAddress?: boolean }): DonationField[] {
  if (content.fields) return content.fields;
  const fields: DonationField[] = [
    { label: "Nama", type: "text", required: true, placeholder: "Nama lengkap" },
    { label: "No. WhatsApp", type: "phone", required: true, placeholder: "08xxxxxxxxxx" },
  ];
  if (content.askAddress !== false) fields.push({ label: "Alamat", type: "textarea", required: true, placeholder: "Alamat lengkap" });
  return fields;
}
